// PipelineCandidatesModal.js
import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { Badge, Button, Divider, styled, Tab, TableBody, TableCell, TableHead, TableRow, Tabs, Tooltip, tooltipClasses } from '@mui/material';
import CandidateProfile from '../models/CandidateProfile';
import { useDispatch, useSelector, } from 'react-redux';
import { getSingleCandidateData } from '../redux/actions/candidateActions';
import moment from 'moment'
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { convertToShortYearAndMonth } from '../helpers/utility';
import StickyTableContainer from '../components/StickyTableContainer';
import { Add, EditNote, Info, Remove, RemoveCircle, RemoveCircleOutline, Timeline } from '@mui/icons-material';
import { getPipelineCandidatesList, removeCandidateFromJobPostPipeline, setJobIdToAddCandidatesInPipeline } from '../redux/actions/jobActions';
import CandidateTimelineModal from './CandidateTimelineModal';
import { formatDateWithTime, formatDDMMYYYYDate } from '../helpers/formattedDateTime';
import constants from '../helpers/constants.json';
import { cardColors } from '../helpers/dashboardColors';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';
import LightTooltip from '../components/LightTooltip';


const PipelineCandidatesModal = ({ isOpen, onClose, jobPostId, selectedStatus }) => {
  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      marginTop: '2em'
    },
    content: {
      left: '5%',
      top: '10%',
      zIndex: 1001,
      width: '94%',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
      overflowY: 'auto',
    },
    spanStyle: {
      color: 'grey',
      fontSize: '14px'
    }
  }
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pipelineCandidatesResponse = useSelector((state) => state.job.pipelineCandidates);
  const pipelineCandidates = pipelineCandidatesResponse?.data;

  const totalRecords = pipelineCandidatesResponse?.metadata?.totalRecords;

  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [isPipelineModalVisible, setIsPipelineModalVisible] = useState(false);
  const [isRemoveFromPipelineConfirmOpen, setIsRemoveFromPipelineConfirmOpen] = useState(false);
  const [tab, setTab] = React.useState(selectedStatus);

  const candidateById = useSelector((state) => state.candidate.candidateById);
  const candidateDataById = candidateById?.data;

  useEffect(() => {
    dispatch(getPipelineCandidatesList(jobPostId));
  }, []);

  useEffect(() => {
    if (selectedCandidate) {
      let updatedCandidate = pipelineCandidates?.find(item => item.jobPostCandidateId == selectedCandidate.jobPostCandidateId);
      if (updatedCandidate) {
        setSelectedCandidate(updatedCandidate);
      }
    }
  }, [pipelineCandidates]);

  const handleCloseCandidateModal = () => {
    setIsCandidateModalOpen(false);
  };

  const openCandidateDetailsModal = (candidate) => {
    dispatch(getSingleCandidateData(candidate.candidateId));
    setIsCandidateModalOpen(true)
  }

  const handleUpdateCandidateStatus = (candidate) => {
    setSelectedCandidate(candidate);
    setIsPipelineModalVisible(true);
  }

  const handleCloseCandidateTimelineModal = () => {
    setIsPipelineModalVisible(false);
    setSelectedCandidate(null);
  }

  const handleChange = (event, newValue) => {
    setTab(newValue);
    dispatch(getPipelineCandidatesList(jobPostId));
  };

  const handleAddCandidatesToPipeline = () => {
    dispatch(setJobIdToAddCandidatesInPipeline(jobPostId));
    navigate(`/app/candidates`);
  }

  const handleRemoveFromPipelineClick = (candidate) => {
    setSelectedCandidate(candidate);
    setIsRemoveFromPipelineConfirmOpen(true);
  }

  const handleRemoveFromPipeline = () => {
    dispatch(removeCandidateFromJobPostPipeline(selectedCandidate.jobPostCandidateId));
    setSelectedCandidate(null);
    setIsRemoveFromPipelineConfirmOpen(false);
  }

  const handleRemoveFromPipelineConfirmClose = () => {
    setIsRemoveFromPipelineConfirmOpen(false);
    setSelectedCandidate(null);
  }

  const statusCounts = pipelineCandidates?.reduce((acc, item) => {
    acc[item.pipelineStatus] = (acc[item.pipelineStatus] || 0) + 1;
    return acc;
  }, {});

  const filteredList =
    tab === "ALL"
      ? pipelineCandidates
      : pipelineCandidates?.filter((candidate) => candidate.pipelineStatus === tab);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Pipeline Candidates Modal"
      style={customStyles}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Pipeline Candidates {totalRecords ? `(${totalRecords})` : ""}</h2>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', columnGap: '16px' }}>
            <div>
              <span style={customStyles.spanStyle}>Job Title: </span><span>{pipelineCandidatesResponse?.context.jobTitle}</span>
            </div>
            <Divider orientation="vertical" flexItem />
            <div>
              <span style={customStyles.spanStyle}>Location: </span><span>{pipelineCandidatesResponse?.context.jobLocation}</span>
            </div>
            <Divider orientation="vertical" flexItem />
            <div>
              <span style={customStyles.spanStyle}>Employer: </span><span>{pipelineCandidatesResponse?.context.employerBrandName}</span>
            </div>
          </div>
          <div style={{ marginRight: '40px' }}>
            <Button variant="contained" color="primary" onClick={handleAddCandidatesToPipeline}>
              <Add style={{ marginRight: '8px' }} />
              Add Candidates To Pipeline
            </Button>
          </div>
        </div>

        <Tabs
          value={tab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          style={{ marginBottom: '12px' }}
          TabIndicatorProps={{
            style: { backgroundColor: cardColors.color, height: '4px', borderRadius: '4px' }
          }}
          sx={{
            "& .MuiTab-root": {
              color: cardColors.color,
              backgroundColor: cardColors.bgColor,
              marginRight: '4px',
              borderRadius: '4px',
              padding: "4px 8px",
              minWidth: "auto",
            },
            "& .MuiTab-root.Mui-selected": {
              fontWeight: 'bold',
              color: "white",
              backgroundColor: "#1976d2",
            }
          }}
        >
          {[{ 'id': 'ALL', 'name': 'All' }, ...constants.allPipelineStatus].map((cat, i) => {
            const badgeCount = statusCounts?.[cat.id] ? statusCounts?.[cat.id] : 0;
            const label = <Badge color="primary" badgeContent={badgeCount}
              sx={{
                "& .MuiBadge-badge": {
                  top: '-8px',
                  borderRadius: '4px',
                  ".Mui-selected &": {
                    backgroundColor: cardColors.bgColor,
                    color: cardColors.color
                  }
                }
              }}>
              {cat.name}
            </Badge>
            return (
              <Tab key={i} label={label} value={cat.id} />
            )
          }
          )}

        </Tabs>

        <StickyTableContainer>
          <TableHead>
            <TableRow>
              <TableCell style={styles.textBold}>Candidate Name</TableCell>
              <TableCell style={styles.textBold}>Location</TableCell>
              <TableCell style={styles.textBold}>Job Location</TableCell>
              <TableCell style={styles.textBold}>Experience</TableCell>
              <TableCell style={styles.textBold}>Highest Education</TableCell>
              <TableCell style={styles.textBold}>Last Course Name</TableCell>
              <TableCell style={styles.textBold}>Applied On Date</TableCell>
              <TableCell style={styles.textBold}>Application Status</TableCell>
              <TableCell style={styles.textBold}>Pipeline Status</TableCell>
              <TableCell style={styles.textBold}>Pipeline Status Date</TableCell>
              <TableCell style={styles.textBold}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList?.map((candidate) => (
              <TableRow key={candidate.candidateId} style={{ cursor: 'pointer' }}>

                <TableCell onClick={() => openCandidateDetailsModal(candidate)}>{candidate.candidateName}</TableCell>
                <TableCell>{candidate.location}</TableCell>
                <TableCell>{candidate.jobLocation}</TableCell>
                <TableCell>{convertToShortYearAndMonth(candidate.monthsOfExperience) || 'No experience'}</TableCell>
                <TableCell>{candidate.highestEducation}</TableCell>
                <TableCell>{candidate.lastCourseName || 'Not specified'}</TableCell>
                <TableCell>{candidate.appliedOnDate ? moment(candidate.appliedOnDate).format("DD/MM/YYYY") : "n/a"}</TableCell>
                <TableCell>{candidate.applicationStatus || 'n/a'}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {candidate.pipelineStatus}
                    {candidate.pipelineStatus == "JOINED" &&
                      <LightTooltip
                        title={
                          <div>
                            <div><strong>Joining Date:</strong> {formatDDMMYYYYDate(candidate.joiningDate)}</div>
                            {candidate.replacementFor &&
                              <div><strong>Replacement For:</strong> {candidate.replacementFor}</div>
                            }
                          </div>
                        }
                      >
                        <IconButton>
                          <Info />
                        </IconButton>
                      </LightTooltip>
                    }
                  </div>
                </TableCell>
                <TableCell>{formatDateWithTime(candidate.pipelineStatusCreateDate)}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', columnGap: '4px' }}>
                    <Tooltip title="Update Pipeline Status">
                      <Button style={{ minWidth: '32px' }} onClick={() => handleUpdateCandidateStatus(candidate)}>
                        <EditNote />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Remove from Pipeline">
                      <Button style={{ minWidth: '32px' }} onClick={() => handleRemoveFromPipelineClick(candidate)}>
                        <RemoveCircleOutline />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StickyTableContainer>


      </div>

      {isCandidateModalOpen && candidateDataById !== null && (
        <CandidateProfile
          isOpen={isCandidateModalOpen}
          onClose={handleCloseCandidateModal}
          candidateData={candidateDataById}
        />
      )}

      {isPipelineModalVisible &&
        <CandidateTimelineModal
          open={true}
          onClose={handleCloseCandidateTimelineModal}
          jobPostId={jobPostId}
          jobTitle={pipelineCandidatesResponse?.context.jobTitle}
          candidate={selectedCandidate}
        />
      }

      {isRemoveFromPipelineConfirmOpen &&
        <ConfirmationDialog
          open={true}
          onClose={handleRemoveFromPipelineConfirmClose}
          onConfirm={handleRemoveFromPipeline}
          message="Are you sure, you want to remove this candidate from pipeline?"
        />
      }


    </ReactModal>


  );
};

export default PipelineCandidatesModal;

const styles = {
  textBold: {
    fontWeight: 'bold'
  }
}